<template>
  <el-dialog
    title="AI智能对话"
    width="800px"
    :visible.sync="visible"
    :close-on-click-modal="false"
    @close="handleDialogClose"
  >
    <!-- 原有的 template 内容保持不变 -->
    <div ref="dialogContent" class="dialog-content">
      <div v-for="(item, index) in messageList" :key="index" class="message-item">
        <div v-if="item.type === 'user'" class="user-message">
          <span class="message-tag">我：</span>
          <span class="message-content">{{ item.content }}</span>
        </div>
        <div v-else class="ai-message">
          <span class="message-tag">AI：</span>
          <span class="message-content" v-html="item.content" />
          <el-button
            v-if="item.type === 'ai' && item.content"
            type="text"
            size="mini"
            :icon="item.isPlaying ? 'el-icon-video-pause' : 'el-icon-video-play'"
            :loading="item.isSynthesizing"
            :disabled="synthesisStatus === '合成中'"
            style="margin-left: 8px;"
            @click="toggleSpeech(item, index)"
          />
        </div>
      </div>
    </div>

    <div v-if="speechStatus !== '未连接'&&speechStatus !== '已连接'" class="speech-status-bar">
      <span v-if="interimResult" class="interim-result">实时识别: {{ interimResult }}</span>
    </div>

    <div v-if="synthesisStatus !== '空闲'" class="speech-status-bar">
      <span class="status-text">语音合成: {{ synthesisStatus }}</span>
    </div>

    <div slot="footer" class="input-area">
      <div class="voice-controls">
        <el-tooltip content="开始录音" placement="top">
          <el-button
            type="success"
            :disabled="isRecording || !currentToken"
            size="small"
            class="voice-btn"
            @click="startRec"
          >
            录音
          </el-button>
        </el-tooltip>

        <el-tooltip content="停止录音" placement="top">
          <el-button
            type="warning"
            :disabled="!isRecording"
            size="small"
            class="voice-btn"
            @click="stopRecording"
          >
            停止
          </el-button>
        </el-tooltip>
      </div>

      <el-input
        v-model="inputValue"
        type="textarea"
        placeholder="请输入您的问题，按回车或点击发送提问..."
        :rows="3"
        :disabled="loading"
        class="input-textarea"
        @keyup.enter.native="sendQuestion"
      />
      <el-button
        type="primary"
        :loading="loading"
        :disabled="loading || !inputValue.trim()"
        class="send-btn"
        @click="sendQuestion"
      >
        发送
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'AiDialog',
  props: {
    visible: { type: Boolean, default: false },
    dialogId: { type: String, default: '' },
    // 语音服务配置
    speechAppKey: {
      type: String,
      default: 'bYgC41rWqjKoRXb3'
    },
    // 修改：token 改为动态获取，但仍保留 prop 用于外部传入（优先级高于自动获取）
    speechToken: {
      type: String,
      default: ''
    },
    speechWebSocketUrl: {
      type: String,
      default: 'wss://nls-gateway.cn-shanghai.aliyuncs.com/ws/v1'
    },
    // 新增：阿里云 AccessKey 配置（用于自动获取 Token）
    accessKeyId: {
      type: String,
      default: process.env.VUE_APP_ALIYUN_ACCESS_KEY_ID
    },
    accessKeySecret: {
      type: String,
      default: process.env.VUE_APP_ALIYUN_ACCESS_KEY_SECRET
    },
    // 新增：是否自动获取 Token（当 speechToken 为空时）
    autoFetchToken: {
      type: Boolean,
      default: true
    },
    // 语音合成配置
    synthesisVoice: {
      type: String,
      default: 'zhimiao_emo'
    },
    synthesisFormat: {
      type: String,
      default: 'pcm'
    },
    synthesisSampleRate: {
      type: Number,
      default: 16000
    }
  },
  data() {
    return {
      // ========== 原有数据 ==========
      inputValue: '',
      messageList: [],
      loading: false,
      abortController: null,
      buffer: '',
      latexBuffer: '',
      decoder: new TextDecoder('UTF-8', { fatal: true, ignoreBOM: true }),

      // 语音识别相关状态
      speechStatus: '未连接',
      isRecording: false,
      isWebSocketConnected: false,
      interimResult: '',
      finalResult: '',
      websocket: null,
      audioContext: null,
      scriptProcessor: null,
      audioInput: null,
      audioStream: null,
      speechTaskId: '',
      hasStartedTranscription: false,

      // 语音合成相关状态
      synthesisStatus: '空闲',
      synthesisWebSocket: null,
      synthesisTaskId: '',
      synthesisAudioContext: null,
      audioBufferSource: null,
      audioQueue: [],
      isAudioPlaying: false,
      currentPlayingIndex: -1,
      playQueueIndex: 0,

      // ========== 新增：Token 管理相关状态 ==========
      currentToken: '', // 当前使用的 Token（外部传入或自动获取）
      tokenExpireTime: 0, // Token 过期时间戳（秒）
      tokenStatus: '空闲', // 空闲/获取中/已获取/失败
      tokenRefreshTimer: null // 定时刷新定时器
    }
  },
  watch: {
    visible(val) {
      if (val) {
        // 对话框打开时初始化 Token
        this.initToken()
      }
    }
  },
  created() {
    // 如果对话框默认显示，也需要初始化
    if (this.visible) {
      this.initToken()
    }
  },
  beforeDestroy() {
    this.handleDialogClose()
    this.disconnectWebSocket()
    this.disconnectSynthesisWebSocket()
    // 清理 Token 定时器
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer)
    }
  },
  methods: {
    // ========== 新增：Token 自动获取相关方法 ==========

    /**
     * 初始化 Token
     * 优先级：外部传入的 speechToken > 自动获取
     */
    async initToken() {
      // 如果外部传入了 Token，直接使用
      if (this.speechToken) {
        this.currentToken = this.speechToken
        this.tokenStatus = '已获取(外部传入)'
        console.log('使用外部传入的 Token')
        return
      }

      // 否则自动获取
      if (this.autoFetchToken && this.accessKeyId && this.accessKeySecret) {
        await this.fetchToken()
      } else {
        this.tokenStatus = '失败(缺少AK配置)'
        console.warn('未配置 AccessKey，无法自动获取 Token')
      }
    },

    /**
     * 获取阿里云 Token（POP API 签名实现）
     */
    async fetchToken() {
      if (!this.accessKeyId || !this.accessKeySecret) {
        this.$message.error('缺少 AccessKey 配置，无法获取 Token')
        return
      }

      this.tokenStatus = '获取中'
      try {
        // 1. 构造请求参数
        const params = {
          AccessKeyId: this.accessKeyId,
          Action: 'CreateToken',
          Version: '2019-02-28',
          Format: 'JSON',
          RegionId: 'cn-shanghai',
          SignatureMethod: 'HMAC-SHA1',
          SignatureVersion: '1.0',
          SignatureNonce: this.generateUUID(),
          Timestamp: this.getISO8601Time()
        }

        // 2. 构造规范化请求字符串
        const canonicalizedQuery = this.canonicalizedQuery(params)

        // 3. 构造签名字符串
        const stringToSign = `GET&${this.percentEncode('/')}&${this.percentEncode(canonicalizedQuery)}`

        // 4. 计算签名 (HMAC-SHA1 + Base64)
        const signature = await this.sign(stringToSign, this.accessKeySecret + '&')

        // 5. 构造完整 URL
        const queryString = `Signature=${signature}&${canonicalizedQuery}`
        const url = `https://nls-meta.cn-shanghai.aliyuncs.com/?${queryString}`

        console.log('获取 Token URL:', url)

        // 6. 发送请求
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()

        if (data.Token) {
          console.log('Token数据结构:', JSON.stringify(data)) // 添加这行
          this.currentToken = data.Token.Id || data.Token // 兼容字符串格式
          this.tokenExpireTime = data.Token.ExpireTime || data.ExpireTime
          this.tokenStatus = '已获取'

          // 计算过期时间（提前5分钟刷新）
          const expireDate = new Date(this.tokenExpireTime * 1000)
          const now = Date.now() / 1000
          const refreshDelay = Math.max((this.tokenExpireTime - now - 300) * 1000, 60000) // 至少1分钟后刷新

          console.log(`Token 获取成功，有效期至: ${expireDate.toLocaleString()}，${Math.floor(refreshDelay / 1000)}秒后刷新`)

          // 设置定时刷新
          if (this.tokenRefreshTimer) clearTimeout(this.tokenRefreshTimer)
          this.tokenRefreshTimer = setTimeout(() => {
            this.fetchToken()
          }, refreshDelay)

          // 更新 WebSocket 状态（如果已连接需要重连）
          if (this.websocket || this.synthesisWebSocket) {
            this.$message.success('Token 已刷新')
          }
        } else if (data.Code) {
          throw new Error(`${data.Code}: ${data.Message}`)
        } else {
          throw new Error('未知的响应格式')
        }
      } catch (error) {
        console.error('获取 Token 失败:', error)
        this.tokenStatus = '失败'
        this.$message.error(`获取语音服务 Token 失败: ${error.message}`)

        // 5分钟后重试
        if (this.tokenRefreshTimer) clearTimeout(this.tokenRefreshTimer)
        this.tokenRefreshTimer = setTimeout(() => {
          this.fetchToken()
        }, 300000)
      }
    },

    /**
     * URL 编码（按照阿里云规则）
     * 规则：UTF-8编码，+替换为%20，*替换为%2A，%7E替换为~
     */
    percentEncode(str) {
      if (!str) return ''
      let encoded = encodeURIComponent(str)
      encoded = encoded.replace(/\+/g, '%20')
        .replace(/\*/g, '%2A')
        .replace(/%7E/g, '~')
      return encoded
    },

    /**
     * 构造规范化请求字符串（参数按字典序排序）
     */
    canonicalizedQuery(params) {
      // 按键排序
      const sortedKeys = Object.keys(params).sort()
      const pairs = []

      for (const key of sortedKeys) {
        const encodedKey = this.percentEncode(key)
        const encodedValue = this.percentEncode(params[key])
        pairs.push(`${encodedKey}=${encodedValue}`)
      }

      return pairs.join('&')
    },

    /**
     * 计算 HMAC-SHA1 签名（使用 Web Crypto API）
     */
    async sign(stringToSign, secret) {
      // 将字符串转换为 ArrayBuffer
      const encoder = new TextEncoder()
      const keyData = encoder.encode(secret)
      const messageData = encoder.encode(stringToSign)

      // 导入密钥
      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'HMAC', hash: 'SHA-1' },
        false,
        ['sign']
      )

      // 签名
      const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData)

      // Base64 编码
      const base64 = btoa(String.fromCharCode(...new Uint8Array(signature)))

      // URL 编码
      return this.percentEncode(base64)
    },

    /**
     * 获取 ISO8601 格式时间戳（UTC）
     * 格式：YYYY-MM-DDTHH:mm:ssZ
     */
    getISO8601Time() {
      const now = new Date()
      const year = now.getUTCFullYear()
      const month = String(now.getUTCMonth() + 1).padStart(2, '0')
      const day = String(now.getUTCDate()).padStart(2, '0')
      const hours = String(now.getUTCHours()).padStart(2, '0')
      const minutes = String(now.getUTCMinutes()).padStart(2, '0')
      const seconds = String(now.getUTCSeconds()).padStart(2, '0')

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`
    },

    generateUUID() {
    // 生成 32 位随机十六进制字符串
      const hex = '0123456789abcdef'
      let uuid = ''
      for (let i = 0; i < 32; i++) {
        uuid += hex[Math.floor(Math.random() * 16)]
      }
      return uuid
    },

    // ========== 修改：WebSocket 连接方法使用 currentToken ==========

    /**
     * 连接语音识别 WebSocket
     */
    connectWebSocket() {
      return new Promise((resolve, reject) => {
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
          this.updateSpeechStatus('已连接')
          resolve()
          return
        }

        const token = this.currentToken || this.speechToken
        if (!token) {
          this.$message.warning('Token 未获取，请等待')
          reject(new Error('Token为空'))
          return
        }

        const appkey = this.speechAppKey || this.$store?.state?.speechAppKey
        if (!appkey) {
          this.$message.warning('请配置语音识别的 AppKey')
          reject(new Error('AppKey为空'))
          return
        }

        const socketUrl = `${this.speechWebSocketUrl}?token=${token}&appkey=${appkey}`
        console.log('连接WebSocket:', socketUrl.replace(token, '***')) // 脱敏日志

        try {
          this.websocket = new WebSocket(socketUrl)
          this.updateSpeechStatus('连接中...')

          // 添加5秒超时
          const timeout = setTimeout(() => {
            if (this.websocket && this.websocket.readyState !== WebSocket.OPEN) {
              this.websocket.close()
              this.websocket = null
              this.updateSpeechStatus('连接超时')
              reject(new Error('WebSocket连接超时（5秒）'))
            }
          }, 5000)

          this.websocket.onopen = () => {
            clearTimeout(timeout)
            this.updateSpeechStatus('已连接')
            console.log('语音识别WebSocket已连接')
            resolve()
          }

          this.websocket.onerror = (error) => {
            clearTimeout(timeout)
            this.updateSpeechStatus('连接错误')
            console.error('WebSocket错误:', error)
            this.$message.error('语音识别连接错误，请检查网络或Token有效性')
            reject(error)
          }

          this.websocket.onclose = (event) => {
            clearTimeout(timeout)
            console.log('WebSocket关闭:', event.code, event.reason)
            this.updateSpeechStatus('未连接')
            this.isRecording = false
            this.hasStartedTranscription = false
            this.websocket = null
          }

          this.websocket.onmessage = (event) => {
            // ... 保持原有onmessage逻辑不变
            try {
              const message = JSON.parse(event.data)
              const headerName = message.header?.name

              if (headerName === "TranscriptionStarted") {
                console.log('语音识别任务已启动')
              } else if (headerName === "TranscriptionResultChanged") {
                this.interimResult = message.payload.result
                console.log('实时识别:', this.interimResult)
              } else if (headerName === "SentenceEnd") {
                this.finalResult = message.payload.result
                if (this.finalResult) {
                  this.inputValue += (this.inputValue ? ' ' : '') + this.finalResult
                  this.interimResult = ''
                  this.$message.success('语音识别完成')
                }
              } else if (headerName === "TranscriptionCompleted") {
                console.log('语音识别任务完成')
                this.hasStartedTranscription = false
              }
            } catch (error) {
              console.error('解析语音识别消息失败:', error)
            }
          }
        } catch (error) {
          this.updateSpeechStatus('未连接')
          reject(error)
        }
      })
    },

    /**
 * 连接语音合成 WebSocket（带超时）
 */
    connectSynthesisWebSocket() {
      return new Promise((resolve, reject) => {
        if (this.synthesisWebSocket && this.synthesisWebSocket.readyState === WebSocket.OPEN) {
          this.synthesisStatus = '已连接'
          resolve()
          return
        }

        const token = this.currentToken || this.speechToken
        if (!token) {
          this.$message.warning('Token 未获取')
          reject(new Error('Token为空'))
          return
        }

        const appkey = this.speechAppKey || this.$store?.state?.speechAppKey
        const socketUrl = `${this.speechWebSocketUrl}?token=${token}&appkey=${appkey}`
        console.log('连接合成WebSocket:', socketUrl.replace(token, '***'))

        try {
          this.synthesisWebSocket = new WebSocket(socketUrl)
          this.synthesisWebSocket.binaryType = 'arraybuffer'
          this.synthesisStatus = '连接中...'

          // 5秒超时
          const timeout = setTimeout(() => {
            if (this.synthesisWebSocket && this.synthesisWebSocket.readyState !== WebSocket.OPEN) {
              this.synthesisWebSocket.close()
              this.synthesisWebSocket = null
              this.synthesisStatus = '连接超时'
              reject(new Error('合成WebSocket连接超时'))
            }
          }, 5000)

          this.synthesisWebSocket.onopen = () => {
            clearTimeout(timeout)
            this.synthesisStatus = '已连接'
            console.log('语音合成WebSocket已连接')
            resolve()
          }

          this.synthesisWebSocket.onerror = (error) => {
            clearTimeout(timeout)
            this.synthesisStatus = '连接错误'
            console.error('合成WebSocket错误:', error)
            this.$message.error('语音合成连接错误')
            reject(error)
          }

          this.synthesisWebSocket.onclose = () => {
            clearTimeout(timeout)
            this.synthesisStatus = '空闲'
            this.synthesisWebSocket = null
          }

          this.synthesisWebSocket.onmessage = this.handleSynthesisMessage
        } catch (error) {
          this.synthesisStatus = '空闲'
          reject(error)
        }
      })
    },

    // ========== 原有方法（保持不变） ==========
    async sendQuestion() {
      const question = this.inputValue.trim()
      if (!question) return

      this.messageList.push({ type: 'user', content: question })
      this.inputValue = ''
      this.loading = true

      const aiMessageIndex = this.messageList.push({ type: 'ai', content: '' }) - 1

      this.$nextTick(() => {
        this.scrollToBottom()
        this.startSSEStream(question, aiMessageIndex)
      })
    },

    async startSSEStream(question, messageIndex) {
      if (this.abortController) {
        this.abortController.abort()
      }

      this.abortController = new AbortController()
      this.buffer = ''
      this.latexBuffer = ''

      try {
        const url = new URL('https://clinic.web.test.yizong.vip/base/ai/qwen/stream-answer')
        url.searchParams.append('question', encodeURIComponent(question))
        if (this.dialogId) {
          url.searchParams.append('dialogId', this.dialogId)
        }
        const response = await fetch(url.href, {
          method: 'GET',
          headers: {
            'Accept': 'text/event-stream',
            'Accept-Charset': 'UTF-8',
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'omit',
          signal: this.abortController.signal,
          keepalive: true,
          cache: 'no-store'
        })

        if (!response.ok) {
          throw new Error(`请求失败，状态码：${response.status}`)
        }

        const contentType = response.headers.get('Content-Type')
        if (!contentType || !contentType.includes('text/event-stream')) {
          throw new Error('服务端未返回SSE流式数据')
        }

        const reader = response.body.getReader()
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read()

          if (done) {
            if (this.buffer.trim()) {
              this.processStreamChunk(this.buffer, messageIndex, true)
            }
            if (this.latexBuffer) {
              const finalContent = this.convertLatexSymbols([this.latexBuffer]).join('')
              if (finalContent) {
                this.messageList[messageIndex].content += finalContent
                this.latexBuffer = ''
              }
            }
            break
          }

          this.buffer += this.decoder.decode(value, { stream: true })

          const result = this.extractCompleteDataSegments(this.buffer)

          if (result.segments.length > 0) {
            result.segments.forEach(segment => {
              this.processStreamChunk(segment, messageIndex, false)
            })
            this.buffer = result.remainder
            this.$nextTick(() => this.scrollToBottom())
          }

          if (this.buffer.length > 5000) {
            this.processStreamChunk(this.buffer, messageIndex, false)
            this.buffer = ''
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('SSE错误：', error)
          this.$message.error('对话连接异常：' + error.message)
        }
      } finally {
        this.loading = false
        this.abortController = null
        this.$nextTick(() => this.scrollToBottom())
      }
    },

    extractCompleteDataSegments(buffer) {
      const segments = []
      const dataIndices = []
      const regex = /data:/g
      let match

      while ((match = regex.exec(buffer)) !== null) {
        dataIndices.push(match.index)
      }

      if (dataIndices.length === 0) {
        return { segments: [], remainder: buffer }
      }

      for (let i = 0; i < dataIndices.length - 1; i++) {
        const start = dataIndices[i]
        const end = dataIndices[i + 1]
        const segment = buffer.substring(start, end).trim()
        if (segment && segment !== 'data:') {
          segments.push(segment)
        }
      }

      const lastIndex = dataIndices[dataIndices.length - 1]
      let remainder = buffer.substring(lastIndex)

      if (remainder.includes('[DONE]')) {
        segments.push(remainder.trim())
        remainder = ''
      } else if (remainder.trim() === 'data:') {
        remainder = ''
      }

      return { segments, remainder }
    },

    processStreamChunk(segment, messageIndex, isFinal = false) {
      const content = segment.replace(/^data:\s*/, '').trim()
      if (!content || content === '[DONE]') return

      const combinedContent = this.latexBuffer + content
      const { completePart, incompletePart } = this.splitCompleteAndIncompleteLatex(combinedContent)

      if (completePart) {
        const convertedContent = this.convertLatexSymbols([completePart]).join('')
        this.messageList[messageIndex].content += convertedContent
      }

      this.latexBuffer = incompletePart

      if (isFinal && this.latexBuffer) {
        const convertedContent = this.convertLatexSymbols([this.latexBuffer]).join('')
        if (convertedContent) {
          this.messageList[messageIndex].content += convertedContent
        }
        this.latexBuffer = ''
      }
    },

    splitCompleteAndIncompleteLatex(content) {
      const latexStartRegex = /\\[a-zA-Z%_\+\-\*\/\{\}]+$/
      const match = content.match(latexStartRegex)

      if (match) {
        const incompletePart = match[0]
        const completePart = content.slice(0, match.index)
        return { completePart, incompletePart }
      } else {
        return { completePart: content, incompletePart: '' }
      }
    },

    convertLatexSymbols(textArr) {
      let text = textArr.join('\n')

      text = text.replace(/\\\\%/g, '%')
      text = text.replace(/\\%/g, '%')
      text = text.replace(/%+/g, '%')

      text = text.replace(/\\\\times/gi, '×')
      text = text.replace(/\\times/gi, '×')
      text = text.replace(/times/gi, '×')

      const latexMap = {
        '\\\\div': '÷', '\\div': '÷',
        '\\\\geq': '≥', '\\geq': '≥',
        '\\\\leq': '≤', '\\leq': '≤',
        '\\\\neq': '≠', '\\neq': '≠',
        '\\\\(': '', '\\)': '',
        '\\\\frac\\{([^\\}]*)\\}\\{([^\\}]*)\\}': '$1/$2',
        '\\frac\\{([^\\}]*)\\}\\{([^\\}]*)\\}': '$1/$2',
        '\\\\sqrt\\{([^\\}]*)\\}': '√$1',
        '\\sqrt\\{([^\\}]*)\\}': '√$1'
      }

      Object.keys(latexMap).forEach(key => {
        const escapedKey = key.replace(/[\\\+\*\?\[\]\^\$\.\{\}\|\(\)]/g, '\\$&')
        const regex = new RegExp(escapedKey, 'gi')
        text = text.replace(regex, latexMap[key])
      })

      text = text.replace(/\[BR\]/gi, '<br>')
      text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')

      return text.split('\n')
    },

    scrollToBottom() {
      const contentDom = this.$refs.dialogContent
      if (contentDom) {
        contentDom.scrollTop = contentDom.scrollHeight
      }
    },

    handleDialogClose() {
      if (this.abortController) {
        this.abortController.abort()
      }
      this.inputValue = ''
      this.messageList = []
      this.loading = false
      this.abortController = null
      this.buffer = ''
      this.latexBuffer = ''

      this.stopRecording()
      this.disconnectWebSocket()

      this.stopAudioPlayback()
      this.disconnectSynthesisWebSocket()
      this.audioQueue = []
      this.currentPlayingIndex = -1
      this.playQueueIndex = 0

      this.messageList.forEach(item => {
        if (item.isPlaying !== undefined) this.$set(item, 'isPlaying', false)
        if (item.isSynthesizing !== undefined) this.$set(item, 'isSynthesizing', false)
      })

      this.$emit('update:visible', false)
    },

    open() {
      this.visible = true
      this.initToken() // 打开时初始化 Token
    },

    updateSpeechStatus(status) {
      this.speechStatus = status
      this.isWebSocketConnected = status === '已连接'
    },

    sendStartTranscription() {
      const appkey = this.speechAppKey || this.$store?.state?.speechAppKey
      this.speechTaskId = this.generateUUID()

      const startTranscriptionMessage = {
        header: {
          appkey: appkey,
          namespace: "SpeechTranscriber",
          name: "StartTranscription",
          task_id: this.speechTaskId,
          message_id: this.generateUUID()
        },
        payload: {
          "format": "pcm",
          "sample_rate": 16000,
          "enable_intermediate_result": true,
          "enable_punctuation_prediction": true,
          "enable_inverse_text_normalization": true
        }
      }

      try {
        this.websocket.send(JSON.stringify(startTranscriptionMessage))
        this.hasStartedTranscription = true
        console.log('已发送开始转录消息')
      } catch (error) {
        console.error('发送开始转录消息失败:', error)
        this.$message.error('启动语音识别失败')
        this.hasStartedTranscription = false
        throw error
      }
    },

    disconnectWebSocket() {
      if (this.websocket) {
        this.websocket.close()
        this.websocket = null
      }
      this.updateSpeechStatus('未连接')
      this.isRecording = false
      this.hasStartedTranscription = false
    },

    async startRec() {
      if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
        try {
          await this.connectWebSocket()
        } catch (error) {
          console.error('连接失败', error)
          return
        }
      }
      this.startRecording()
    },

    async startRecording() {
      if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
        this.$message.warning('请先连接语音识别服务')
        return
      }

      if (this.isRecording) {
        this.stopRecording()
        await new Promise(resolve => setTimeout(resolve, 100))
      }

      try {
        if (!this.hasStartedTranscription) {
          this.sendStartTranscription()
          await new Promise(resolve => setTimeout(resolve, 300))
        }

        this.audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 16000,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true
          }
        })

        this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: 16000
        })

        this.audioInput = this.audioContext.createMediaStreamSource(this.audioStream)
        this.scriptProcessor = this.audioContext.createScriptProcessor(2048, 1, 1)

        this.scriptProcessor.onaudioprocess = (event) => {
          if (!this.isRecording || !this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
            return
          }

          const inputData = event.inputBuffer.getChannelData(0)
          const inputData16 = new Int16Array(inputData.length)

          for (let i = 0; i < inputData.length; ++i) {
            inputData16[i] = Math.max(-1, Math.min(1, inputData[i])) * 0x7FFF
          }

          if (this.websocket.readyState === WebSocket.OPEN) {
            this.websocket.send(inputData16.buffer)
          }
        }

        this.audioInput.connect(this.scriptProcessor)
        this.scriptProcessor.connect(this.audioContext.destination)

        this.isRecording = true
        this.updateSpeechStatus('录音中...')
        this.$message.success('开始录音')
      } catch (error) {
        console.error('录音失败:', error)
        this.$message.error('录音失败: ' + error.message)
        this.isRecording = false
        this.hasStartedTranscription = false
      }
    },

    stopRecording() {
      if (this.scriptProcessor) {
        this.scriptProcessor.disconnect()
        this.scriptProcessor = null
      }
      if (this.audioInput) {
        this.audioInput.disconnect()
        this.audioInput = null
      }
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(track => track.stop())
        this.audioStream = null
      }
      if (this.audioContext) {
        this.audioContext.close()
        this.audioContext = null
      }

      this.isRecording = false
      this.interimResult = ''

      if (this.websocket && this.websocket.readyState === WebSocket.OPEN && this.hasStartedTranscription) {
        const stopMessage = {
          header: {
            message_id: this.generateUUID(),
            task_id: this.speechTaskId,
            namespace: "SpeechTranscriber",
            name: "StopTranscription",
            appkey: this.speechAppKey || this.$store?.state?.speechAppKey
          },
          payload: {}
        }

        try {
          this.websocket.send(JSON.stringify(stopMessage))
          console.log('已发送停止转录消息')
        } catch (error) {
          console.error('发送停止录音消息失败:', error)
        }

        this.updateSpeechStatus('已连接')
      }
      this.disconnectWebSocket()
    },

    handleSynthesisMessage(event) {
      if (typeof event.data === 'string') {
        try {
          const message = JSON.parse(event.data)
          const headerName = message.header?.name

          if (headerName === 'SynthesisStarted') {
            console.log('✅ 语音合成任务已启动，task_id:', message.header.task_id)
            this.synthesisStatus = '合成中'
            this.audioQueue = []
            this.playQueueIndex = 0
            return
          }

          if (headerName === 'SynthesisCompleted') {
            console.log('✅ 语音合成完成', message.header.task_id)
            this.synthesisStatus = '已完成'

            if (this.currentPlayingIndex !== -1) {
              this.$set(this.messageList[this.currentPlayingIndex], 'isSynthesizing', false)
            }

            setTimeout(() => {
              if (this.audioQueue.length === 0 && !this.isAudioPlaying) {
                this.disconnectSynthesisWebSocket()
              }
            }, 1000)
          } else if (headerName === 'TaskFailed') {
            if (this.currentPlayingIndex !== -1) {
              this.$set(this.messageList[this.currentPlayingIndex], 'isSynthesizing', false)
            }
          }
        } catch (error) {
          console.error('解析合成消息失败:', error)
        }
      } else {
        if (event.data && event.data.byteLength > 0) {
          this.handleAudioData(event.data)
        } else {
          console.warn('语音合成：收到空音频数据')
        }
      }
    },

    handleAudioData(audioData) {
      if (audioData && audioData.byteLength > 0) {
        this.audioQueue.push(audioData)
        if (!this.isAudioPlaying) {
          this.playAudioQueue()
        }
      } else {
        console.warn('语音合成：收到空音频数据，已忽略')
      }
    },

    async playAudioQueue() {
      if (this.audioQueue.length === 0) {
        this.isAudioPlaying = false
        this.synthesisStatus = '空闲'
        if (this.currentPlayingIndex !== -1) {
          this.$set(this.messageList[this.currentPlayingIndex], 'isPlaying', false)
        }
        return
      }

      this.isAudioPlaying = true
      this.synthesisStatus = '播放中'

      if (this.currentPlayingIndex !== -1) {
        this.$set(this.messageList[this.currentPlayingIndex], 'isPlaying', true)
      }

      const pcmBuffer = this.audioQueue.shift()

      if (!pcmBuffer || pcmBuffer.byteLength === 0) {
        console.warn('跳过空数据块')
        setTimeout(() => this.playAudioQueue(), 10)
        return
      }

      try {
        if (!this.synthesisAudioContext) {
          this.synthesisAudioContext = new (window.AudioContext || window.webkitAudioContext)({
            sampleRate: 16000
          })
        }

        if (this.synthesisAudioContext.state === 'suspended') {
          await this.synthesisAudioContext.resume()
        }

        const pcmData = new Int16Array(pcmBuffer)
        const float32Data = new Float32Array(pcmData.length)

        for (let i = 0; i < pcmData.length; i++) {
          float32Data[i] = pcmData[i] / 32768.0
        }

        const audioBuffer = this.synthesisAudioContext.createBuffer(
          1,
          float32Data.length,
          16000
        )

        audioBuffer.copyToChannel(float32Data, 0)

        this.audioBufferSource = this.synthesisAudioContext.createBufferSource()
        this.audioBufferSource.buffer = audioBuffer
        this.audioBufferSource.connect(this.synthesisAudioContext.destination)

        this.audioBufferSource.onended = () => {
          this.playQueueIndex++
          setTimeout(() => this.playAudioQueue(), 5)
        }

        this.audioBufferSource.start(0)
        console.log('▶️ 播放音频块:', this.playQueueIndex, '长度:', float32Data.length)
      } catch (error) {
        console.error('播放音频失败:', error)
        this.$message.error('语音播放失败')
        setTimeout(() => this.playAudioQueue(), 10)
      }
    },

    stopAudioPlayback() {
      if (this.audioBufferSource) {
        try {
          this.audioBufferSource.stop()
          this.audioBufferSource.disconnect()
        } catch (e) {
          console.warning(e)
        }
        this.audioBufferSource = null
      }

      this.isAudioPlaying = false
      this.audioQueue = []
      this.currentPlayingIndex = -1
      this.playQueueIndex = 0

      this.messageList.forEach((item, index) => {
        if (item.isPlaying) {
          this.$set(item, 'isPlaying', false)
        }
      })

      this.synthesisStatus = '空闲'
    },

    async synthesizeSpeech(text, messageIndex) {
      if (!text || text.trim() === '') {
        this.$message.warning('没有可合成的文本')
        return
      }

      text = text.replace(/<br\s*\/?>/gi, ' ')
      text = text.replace(/<[^>]+>/g, '')
      const textarea = document.createElement('textarea')
      textarea.innerHTML = text
      text = textarea.value
      text = text.replace(/[\n\r\t]/g, ' ')
      text = text.replace(/\s+/g, ' ').trim()

      if (text.length > 300) {
        this.$message.warning('文本过长，将截断前300字符')
        text = text.slice(0, 300)
      }

      try {
        await this.connectSynthesisWebSocket()

        this.stopAudioPlayback()

        this.synthesisStatus = '合成中'
        this.currentPlayingIndex = messageIndex
        this.playQueueIndex = 0

        this.$set(this.messageList[messageIndex], 'isSynthesizing', true)
        this.$set(this.messageList[messageIndex], 'isPlaying', false)

        this.synthesisTaskId = this.generateUUID()
        const appkey = this.speechAppKey || this.$store?.state?.speechAppKey

        const startSynthesisMessage = {
          header: {
            appkey: appkey,
            namespace: "SpeechSynthesizer",
            name: "StartSynthesis",
            task_id: this.synthesisTaskId,
            message_id: this.generateUUID()
          },
          payload: {
            text: text,
            voice: this.synthesisVoice,
            format: "pcm",
            sample_rate: 16000,
            volume: 50,
            speech_rate: 0,
            pitch_rate: 0,
            enable_subtitle: false
          }
        }

        this.synthesisWebSocket.send(JSON.stringify(startSynthesisMessage))

        setTimeout(() => {
          if (this.synthesisWebSocket?.readyState === WebSocket.OPEN) {
            const stopMessage = {
              header: {
                appkey: appkey,
                task_id: this.synthesisTaskId,
                message_id: this.generateUUID(),
                namespace: "SpeechSynthesizer",
                name: "StopSynthesis"
              },
              payload: {}
            }
            this.synthesisWebSocket.send(JSON.stringify(stopMessage))
            console.log('已发送 StopSynthesis 指令')
          }
        }, 100)
      } catch (error) {
        console.error('启动语音合成失败:', error)
        this.$message.error('启动语音合成失败')

        if (messageIndex !== -1) {
          this.$set(this.messageList[messageIndex], 'isSynthesizing', false)
        }
        this.synthesisStatus = '空闲'
      }
    },

    toggleSpeech(messageItem, index) {
      if (messageItem.isPlaying) {
        this.stopAudioPlayback()
        return
      }

      this.messageList.forEach((item, i) => {
        if (i !== index && item.isPlaying) {
          this.$set(item, 'isPlaying', false)
        }
      })

      if (!messageItem.isSynthesizing) {
        this.synthesizeSpeech(messageItem.content, index)
      } else {
        this.$message.info('语音合成中，请稍候')
      }
    },

    disconnectSynthesisWebSocket() {
      if (this.synthesisWebSocket) {
        if (this.synthesisStatus === '合成中' && this.synthesisTaskId) {
          try {
            const stopMessage = {
              header: {
                appkey: this.speechAppKey || this.$store?.state?.speechAppKey,
                task_id: this.synthesisTaskId,
                message_id: this.generateUUID(),
                namespace: "SpeechSynthesizer",
                name: "StopSynthesis"
              },
              payload: {}
            }
            this.synthesisWebSocket.send(JSON.stringify(stopMessage))
          } catch (e) {
            console.warn('发送停止合成消息失败', e)
          }
        }

        this.synthesisWebSocket.close()
        this.synthesisWebSocket = null
      }

      if (this.synthesisAudioContext) {
        this.synthesisAudioContext.close()
        this.synthesisAudioContext = null
      }

      this.synthesisStatus = '空闲'
    }
  }
}
</script>

<style scoped lang="scss">
.dialog-content {
  height: 400px;
  overflow-y: auto;
  padding: 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  margin-bottom: 15px;
  background-color: #fafafa;

  .message-item {
    margin-bottom: 15px;
    overflow: hidden;
    &:last-child {
      margin-bottom: 0;
    }

    .message-tag {
      font-weight: 600;
      display: inline-block;
      margin-right: 8px;
      margin-bottom: 4px;
    }

    .message-content {
      line-height: 1.6;
      padding: 8px 12px;
      border-radius: 6px;
      max-width: 80%;
      word-wrap: break-word;
      word-break: break-all;
    }
  }

  .user-message {
    text-align: right;
    .message-tag {
      color: #409eff;
    }
    .message-content {
      background-color: #409eff;
      color: #fff;
      display: inline-block;
    }
  }

  .ai-message {
    text-align: left;
    .message-tag {
      color: #67c23a;
    }
    .message-content {
      background-color: #e6f4ea;
      color: #333;
      display: inline-block;
    }
  }
}

.speech-status-bar {
  background-color: #f0f9ff;
  border: 1px solid #c8e6ff;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 15px;
  font-size: 14px;

  .status-text {
    color: #409eff;
    font-weight: 500;
  }

  .interim-result {
    margin-left: 15px;
    color: #f56c6c;
    font-style: italic;
  }
}

.voice-controls {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;

  .voice-btn {
    flex: 1;
  }
}

.input-area {
  display: flex;
  flex-direction: column;
  gap: 10px;

  .input-textarea {
    width: 100%;
  }

  .send-btn {
    align-self: flex-end;
    min-width: 80px;
  }
}
</style>
